import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowsInSimple,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";

import { AssetViewerPane, type AssetFile } from "./AssetViewerPane";
import { ChevronNavButton } from "./ChevronNavButton";

import { ticketRoute } from "../../tickets/routes/ticket/route";
import { useTicketViewData } from "../hooks/useTicketView";

interface TicketViewPaneProps {
  ticketId: string;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  reviewStatus?: string | null;
  onCitationClick?: (
    pageNumber: number,
    position?: { x: number; y: number },
  ) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  /**
   * Sequential review navigation. When provided alongside totalReviews > 1,
   * a "Review N of M" strip renders below the header. Only set on the
   * sequential single-review TicketViewPane instance — not the project
   * drill-in instance.
   */
  reviewIndex?: number;
  totalReviews?: number;
  onPrevReview?: () => void;
  onNextReview?: () => void;
  /**
   * True while the Vitalora killer-flow typewriter is still revealing
   * the parallel review. When set, the asset image renders without any
   * pins or comment carousel and the header swaps the resolved status
   * badges for a "Reviewing…" pill. Findings appear together once the
   * chat narration finishes — they must NOT spoil the reveal at t=0.
   *
   * Mirrors `isKillerFlowStreaming` on `ProjectStatusPane` so single
   * and multi-asset killer-flow chats share the same gate.
   */
  isKillerFlowStreaming?: boolean;
}

const STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

export function TicketViewPane({
  ticketId,
  onCollapse,
  onBackToProject,
  reviewStatus,
  onCitationClick,
  onPinDragEnd,
  reviewIndex,
  totalReviews,
  onPrevReview,
  onNextReview,
  isKillerFlowStreaming = false,
}: TicketViewPaneProps) {
  const router = useRouter();
  // Build through the router so the URL respects the configured basepath
  // (e.g. `/ticket-canvas-shapes-guard` on epic environments). A raw
  // `/tickets/...` href bypasses the SPA's basepath and lands the
  // new tab on the wrong app root.
  const ticketHref = router.buildLocation({
    to: ticketRoute.to,
    params: { ticketId },
    search: { version: 0 },
  }).href;
  const { ticket, comments, isLoading } = useTicketViewData(ticketId);

  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
      }
    },
    [],
  );

  const latestBoard = ticket?.revisionBoards
    ?.filter((b) => !b.deletedAt)
    .at(-1);
  const reviewFiles = latestBoard?.reviewFiles ?? [];
  const thumbnails = ticket?.thumbnails ?? [];

  // Defense-in-depth: the christo backend filters isDeleted in
  // getTicketComments, but if a stale comment slips through (older
  // client cache, polling race) we must not render pins for deleted
  // comments on the asset viewer. CommentsList and TicketDetailsDrawer
  // already filter; mirror that here so the canvas pin layer stays
  // consistent with the carousel.
  const activeComments = useMemo(
    () => (comments ?? []).filter((c) => !c.isDeleted),
    [comments],
  );

  // Stabilize the assets array identity: ImageViewer resets scroll position
  // when `assets` changes, so an unstable reference on every poll can snap
  // the viewport back to the top mid-scroll. Memo must run unconditionally
  // (no early returns above) to satisfy the rules of hooks.
  const assets: AssetFile[] = useMemo(() => {
    if (reviewFiles.length > 0) return reviewFiles;
    return thumbnails.map((src, i) => ({
      source: src,
      name: `Page ${i + 1}`,
    }));
  }, [reviewFiles, thumbnails]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Flex direction="column" align="center" gap="2">
          <Spinner size="3" />
          <Text size="2" className="text-puntt-neutral-gray-9">
            Loading ticket...
          </Text>
        </Flex>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          Ticket not found
        </Text>
      </div>
    );
  }

  const status = ticket.status ?? "wip";

  return (
    <div
      className="flex h-full flex-col bg-base-white"
      data-testid="ticket-view-pane"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="h-10 shrink-0 border-b border-puntt-neutral-gray-6 px-4"
      >
        <Flex align="center" gap="2" className="min-w-0 flex-1">
          {onBackToProject && (
            <Button
              data-testid="ticket-view-pane-button-173"
              size="1"
              variant="ghost"
              onClick={onBackToProject}
              className="shrink-0 cursor-pointer"
              aria-label="Back to project"
            >
              <ArrowLeft size={14} />
            </Button>
          )}
          <Text
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
          >
            {ticket.title}
          </Text>
          {isKillerFlowStreaming ? (
            <Badge
              size="1"
              variant="soft"
              color="amber"
              data-testid="ticket-view-pane-reviewing-pill"
            >
              <span className="mr-1 inline-block size-1.5 animate-pulse rounded-full bg-puntt-gold-9 align-middle" />
              Reviewing…
            </Badge>
          ) : (
            <>
              {(!latestBoard?.isAIReviewed || status !== "wip") && (
                <Badge
                  size="1"
                  variant="soft"
                  color={STATUS_COLORS[status] ?? "gray"}
                >
                  {STATUS_LABELS[status] ?? status}
                </Badge>
              )}
              {latestBoard?.isAIReviewed && (
                <Badge size="1" variant="soft" color="blue">
                  AI Reviewed
                </Badge>
              )}
            </>
          )}
          {reviewStatus && !isKillerFlowStreaming && (
            <Text
              size="1"
              className={`italic ${
                reviewStatus === "Review complete"
                  ? "text-malachite-700"
                  : "animate-pulse text-puntt-accent-9"
              }`}
            >
              {reviewStatus}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="1" className="shrink-0">
          <Tooltip content="Open in full review">
            <Button
              size="1"
              variant="ghost"
              asChild
              className="cursor-pointer"
              aria-label="Open in full review"
            >
              <a
                data-testid="ticket-view-pane-a-224"
                href={ticketHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ArrowSquareOut size={14} />
              </a>
            </Button>
          </Tooltip>
          {onCollapse && (
            <Tooltip content="Collapse pane">
              <Button
                data-testid="ticket-view-pane-button-231"
                size="1"
                variant="ghost"
                onClick={onCollapse}
                className="cursor-pointer"
                aria-label="Collapse pane"
              >
                <ArrowsInSimple size={14} />
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {/* Sequential review navigation */}
      {(totalReviews ?? 0) > 1 && (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 border-b border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-3 py-1.5"
          data-testid="review-nav-strip"
        >
          <ChevronNavButton
            direction="left"
            onClick={onPrevReview}
            disabled={reviewIndex === 0}
            aria-label="Previous review"
            data-testid="review-nav-prev"
          />
          <Text size="1" className="text-puntt-neutral-gray-11">
            Review {(reviewIndex ?? 0) + 1} of {totalReviews}
          </Text>
          <ChevronNavButton
            direction="right"
            onClick={onNextReview}
            disabled={reviewIndex === (totalReviews ?? 1) - 1}
            aria-label="Next review"
            data-testid="review-nav-next"
          />
        </Flex>
      )}

      {/* Body */}
      <div className="min-h-0 flex-1">
        <AssetViewerPane
          // Reset internal viewer state (incl. Office snapshot) per ticket so
          // the spreadsheet → image transition on one ticket can't carry over
          // to the next when this pane is reused for a different ticket.
          key={ticketId}
          title=""
          assets={assets}
          comments={isKillerFlowStreaming ? [] : activeComments}
          onCitationClick={onCitationClick}
          onPinDragEnd={isKillerFlowStreaming ? undefined : onPinDragEnd}
          activeCommentId={isKillerFlowStreaming ? null : selectedCommentId}
          onCommentClick={
            isKillerFlowStreaming ? undefined : handleCommentClick
          }
          onPinClick={isKillerFlowStreaming ? undefined : handleCommentClick}
          highlightPosition={isKillerFlowStreaming ? null : highlightPing}
          ticketId={ticketId}
        />
      </div>
    </div>
  );
}
